/**
 * strokeStats — per-swim, per-stroke stats cache.
 *
 * Key: "swimset.strokeStats.v2"  (NEVER folded into setsDiscovered.v2 — different
 * concerns; keeping them separate keeps the later native producer swap clean.)
 * Schema: { version, entries: Record<swimUuid, StrokeWorkoutStats> }
 *
 * Write paths:
 *   B — Prototypes.svelte: calls recordStrokeStats after deriveWorkout resolves
 *   C — setsDiscovered.ts: called from scanHistory's loop via the onDerived
 *       callback (reuses the already-fetched firehose; no second fetch)
 *
 * Read: ScoresTab.svelte reads getAllStrokeStats() and feeds
 * buildStrokeTrendViewModel (strokeTrend.ts).
 */
import type { StrokeWorkoutStats, PerStrokeStats } from "./strokeTrend";
import type { DerivedWorkout } from "./lapAnalysis";

export const STROKE_STATS_CACHE_KEY = "swimset.strokeStats.v2";
export const STROKE_STATS_VERSION = 2;

export interface StrokeStatsCache {
  version: number;
  entries: Record<string, StrokeWorkoutStats>;
}

// The HealthKit stroke-style strings we track. Freestyle is the only one the
// trend reads today; the rest are stored now so the schema is stable when other
// strokes graduate to their own trends later.
const TRACKED_STROKES = ["Freestyle", "Breaststroke", "Backstroke", "Butterfly"] as const;
type TrackedStroke = (typeof TRACKED_STROKES)[number];

// ─── Persistence ─────────────────────────────────────────────────────────────

function emptyCache(): StrokeStatsCache {
  return { version: STROKE_STATS_VERSION, entries: {} };
}

export function loadStrokeStatsCache(): StrokeStatsCache {
  try {
    const raw =
      typeof localStorage === "undefined" ? null : localStorage.getItem(STROKE_STATS_CACHE_KEY);
    if (!raw) return emptyCache();
    const parsed = JSON.parse(raw) as Partial<StrokeStatsCache>;
    if (
      parsed.version !== STROKE_STATS_VERSION ||
      typeof parsed.entries !== "object" ||
      parsed.entries === null
    ) {
      return emptyCache();
    }
    return {
      version: STROKE_STATS_VERSION,
      entries: parsed.entries as Record<string, StrokeWorkoutStats>,
    };
  } catch {
    return emptyCache();
  }
}

export function saveStrokeStatsCache(cache: StrokeStatsCache): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STROKE_STATS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota / private-mode / purged — degrades to in-session only (known debt) */
  }
}

// ─── Public read API ─────────────────────────────────────────────────────────

/** All indexed per-swim stats as a flat array. The trend builder filters + sorts. */
export function getAllStrokeStats(): StrokeWorkoutStats[] {
  return Object.values(loadStrokeStatsCache().entries);
}

/**
 * Has this swim's strokeStats side-write already succeeded? Lets
 * setsDiscovered.scanHistory (ScanDeps.hasDerived) tell a workout that's
 * already counted into the sets ledger apart from one whose onDerived
 * side-write into THIS cache never landed (deriveWorkout/recordStrokeStats
 * threw) — the former should not be re-fetched, the latter should retry on
 * the next scan (Codex-Medium, wave-1 cross-vendor review).
 */
export function hasStrokeStats(swimUuid: string): boolean {
  return swimUuid in loadStrokeStatsCache().entries;
}

// ─── Write ───────────────────────────────────────────────────────────────────

/**
 * Extract per-stroke stats from a derived workout and UPSERT into the cache.
 * Idempotent: recording the same swimUuid again overwrites (last write wins).
 * Safe: a swim with no swolfStats or no SWOLF data for a stroke stores what it
 * has and never throws.
 *
 * glideAvg comes from swolfStats.perStrokeAvg[stroke].avgSwolf (rounded);
 * meters + lengthCount come from strokeBreakdown[stroke] (the true length count;
 * swolfStats.lapCount only counts laps that carried a SWOLF score).
 *
 * @param derived   from deriveWorkout()
 * @param swimUuid  workout UUID (fh.workout.uuid)
 * @param swimDate  ISO date/timestamp (fh.workout.startDate); normalized to YYYY-MM-DD
 */
export function recordStrokeStats(
  derived: DerivedWorkout,
  swimUuid: string,
  swimDate: string,
): void {
  const byStroke: NonNullable<StrokeWorkoutStats["byStroke"]> = {};

  for (const strokeName of TRACKED_STROKES) {
    const swolfEntry = derived.swolfStats?.perStrokeAvg.find((e) => e.stroke === strokeName);
    const bdEntry = derived.strokeBreakdown.find((e) => e.style === strokeName);
    if (!swolfEntry || !bdEntry) continue; // omit strokes without both signals

    const stats: PerStrokeStats = {
      glideAvg: Math.round(swolfEntry.avgSwolf),
      avgStrokes: Math.round(swolfEntry.avgStrokes),
      avgSeconds: Math.round(swolfEntry.avgSeconds),
      meters: bdEntry.meters,
      lengthCount: bdEntry.lengths,
    };
    (byStroke as Record<TrackedStroke, PerStrokeStats>)[strokeName] = stats;
  }

  const entry: StrokeWorkoutStats = {
    swimUuid,
    swimDate: swimDate.slice(0, 10), // normalize to YYYY-MM-DD
    freestyle: byStroke.Freestyle, // convenience alias for the view-model
    byStroke,
  };

  const cache = loadStrokeStatsCache();
  cache.entries[swimUuid] = entry;
  saveStrokeStatsCache(cache);
}
